"use client"
import dynamic from "next/dynamic";
import type { Reducer } from "react";
import { useReducer } from "react";
import FilmwebIdButtonPanel from "src/components/film/filmwebid/FilmwebIdButtonPanel";
import { MovieActivityWrapper } from "src/components/film/filmwebid/MovieActivityContext";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import CameraCircleSvg from "src/components/svg/CameraCircleSvg";
import PlaySvg from "src/components/svg/PlaySvg";
import TrailerOverlayWrapper from "src/components/traileroverlaywrapper/TrailerOverlayWrapper";
import type IPremiumLandingPage from "src/lib/types/IPremiumLandingPage";
import { getMovieIdentifier } from "src/lib/filmwebid/useMovieIdentifier";
import styles from "./ExtraProgramControls.module.css";
import type { ITrailer } from "src/lib/types/Trailer";

const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND;

const DynamicImageGalleryDisplay = dynamic(() => import('src/components/imagegallery/ImageGalleryDisplay'), {
    loading: () => <LoadingIndicator />,
});

//#region [Props]
type ExtraProgramControlsProps = {
    landingpage: IPremiumLandingPage;
    maxUserRatingValue: number;
};
//#endregion

//#region [Component]
export default function ExtraProgramControls({ landingpage, maxUserRatingValue }: ExtraProgramControlsProps) {
    const [state, dispatch] = useReducer(reducer, { isShowingGallery: false, isShowingTrailer: false } as ReducerState)
    const movieIdentifier = getMovieIdentifier(landingpage.movie);

    return <>
        <div className={styles.SExtraProgramControls}>
            <MovieActivityWrapper movieIdentifier={movieIdentifier}>
                <FilmwebIdButtonPanel className={styles.SFilmwebIdButtonPanel} movieIdentifier={movieIdentifier}
                    hideUserInteractionStatus
                    hideCommenting
                    hideGiveRating
                    maxUserRatingValue={maxUserRatingValue}
                />

            </MovieActivityWrapper>
            {(landingpage.movie.trailers?.[0] as ITrailer)?.videoId && <>

                <button className={styles.STrailerButton} onClick={() => dispatch("showTrailer")}>
                    <PlaySvg />
                    <span className={styles.SIconText}>Trailer</span>
                </button>
            </>}
            {(landingpage.movie.imagesV2?.length ?? 0) >= 2 && <button className={styles.SGalleryButton} onClick={() => dispatch("showGallery")}>
                <CameraCircleSvg className={styles.SGallerySvg} />
                <span className={styles.SIconText}>Bilder</span>
            </button>}

        </div>
        {state.isShowingTrailer && (landingpage.movie.trailers?.length ?? 0) > 0 && <TrailerOverlayWrapper onClose={() => dispatch("close")} videoId={Number((landingpage.movie.trailers![0] as ITrailer)!.videoId)} hideRelated />}
        {state.isShowingGallery && landingpage.movie.imagesV2 && <DynamicImageGalleryDisplay
            images={landingpage.movie.imagesV2}
            onClose={() => dispatch("close")} />}
    </>
}
//#endregion

type ReducerState = {
    isShowingTrailer: boolean;
    isShowingGallery: boolean;
}

const reducer: Reducer<ReducerState, "close" | "showGallery" | "showTrailer"> = (state, action) => {
    switch (action) {
        case "showGallery":
            return { ...state, isShowingGallery: true, isShowingTrailer: false };
        case "showTrailer":
            return { ...state, isShowingGallery: false, isShowingTrailer: true };
        case "close":
            return { ...state, isShowingGallery: false, isShowingTrailer: false };
        default:
            return state;
    }

}

//#region [Styles]
//#endregion
